import axios from 'axios'
import { getApiOrigin } from '@/utils/backendEnv.js'
import { API_CONFIG } from '@/config/api.js'
import { shouldAvoidLoginRedirect, requestAuthFromParent } from '@/utils/embedContext'
import { tokenService } from '@/services/tokenService'

const API_ORIGIN = getApiOrigin()
// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_ORIGIN,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true, // Important for session cookies
  headers: {
    'Content-Type': 'application/json',
  }
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {

    // Prefer OAuth/JWT access token first for SimpleJWT endpoints (e.g. /api/v1/management/*),
    // then fall back to legacy session_token for modules still using custom JWT auth.
    // tokenService checks sessionStorage first (where verifyOtp stores it), then localStorage.
    const token =
      tokenService.getToken() ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('session_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // For FormData requests, let the browser set the Content-Type header
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {

    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {

    return response
  },
  (error) => {

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      const embed = shouldAvoidLoginRedirect()
      if (embed) {
        requestAuthFromParent()
        return Promise.reject(error)
      }
      tokenService.clearTokens()
      localStorage.removeItem('session_token')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('current_user')
      if (
        window.location.pathname !== '/login' &&
        !window.location.pathname.includes('/vendor-login')
      ) {
        window.location.href = '/login'
      }
    }
    
    // Handle 403 Forbidden - permission denied (do not hard-redirect vendor risk workflows: show inline error instead)
    if (error.response?.status === 403) {
      const url = String(error.config?.url || '')
      // Base URL is relative: /api/v1/management/vendor-risks/:id/mitigate/
      const isVendorRiskMutation = /\/vendor-risks\/[^/]+\/(mitigate|reject|record)\b/.test(url)
      const isVendorScreeningRead = /screening-results\/vendor_screening_results\b/.test(url)
      if (!isVendorRiskMutation && !isVendorScreeningRead) {
        const errorData = error.response.data
        const d = errorData?.detail
        const errorMessage =
          (typeof d === 'string' && d) ||
          errorData?.error ||
          errorData?.message ||
          'You do not have permission to access this resource.'
        const errorCode = errorData?.code || '403'

        sessionStorage.setItem(
          'access_denied_error',
          JSON.stringify({
            message: errorMessage,
            code: errorCode,
            timestamp: new Date().toISOString(),
            path: window.location.pathname,
          })
        )

        if (!shouldAvoidLoginRedirect() && window.location.pathname !== '/access-denied') {

          window.location.href = '/access-denied'
        }
      }
    }
    
    return Promise.reject(error)
  }
)

export default apiClient
